"use client";

import { useEffect, useState, useCallback } from "react";
import { Table, TableColumnsType } from "antd";
import PaymentDetail from "../payment-detail/index";
import RefundDetail from "../payment-detail/RefundDetail";
import { useTranslation } from "react-i18next";
import ActionService from "../../../core/services/action";
import { printSafely, OrderData } from "../print-invoice/print-utils";
import { toast } from "react-toastify";
import useConfigEnv from "../../../zustand";

type Props = {
  studentId: any;
  partnerId: any;
  applicationID: any;
};

interface DataType {
  key: React.Key;
  id: number;
  order: string;
  code: string;
  narration: string;
  amount: string;
  amount_residual: string | number;
  status: string;
  dateDue: string;
  programDueDate: string;
  anumTuition: string;
  semesterName: string;
  paymentUrl: string;
  isSurchargeOverdue: boolean;
  net_total_after_credit: number;
}

// Convert API response to OrderData format
const convertToOrderData = (apiData: any): OrderData => {
  // console.log("Converting API data:", apiData);

  // Handle invoice payments
  let invoicesPayment = [];
  try {
    if (
      apiData.invoice_payments_widget?.content &&
      Array.isArray(apiData.invoice_payments_widget.content)
    ) {
      invoicesPayment = apiData.invoice_payments_widget.content.map(
        (payment: any) => ({
          create_date: payment.create_date || new Date().toISOString(),
          amount: payment.amount || 0,
          journal_name: payment.journal_name || "Unknown",
          move_type: payment?.move_type,
        })
      );
    }
  } catch (error) {
    console.warn("Error processing invoice payments:", error);
    invoicesPayment = [];
  }

  // Create OrderData with proper mapping
  const orderData: OrderData = {
    id: apiData.id || 0,
    partner_id: {
      id: apiData.partner_id?.id?.toString() || "0",
      display_name: apiData.partner_id?.display_name || "Unknown Student",
      stud_id: apiData.partner_id?.stud_id || "",
      tmp_stud_id: apiData.partner_id?.tmp_stud_id || false,
    },
    company: {
      logo: apiData.company_id?.logo || "",
      name: apiData.company_id?.name || "Unknown Company",
      street: apiData.company_id?.street || "",
      street2: apiData.company_id?.street2 || "",
      city: apiData.company_id?.city || "",
    },
    tuition_program_id: {
      display_name:
        apiData.tuition_program_id?.display_name || "Unknown Program",
      name: apiData.tuition_program_id?.name || "Unknown Program",
      is_surcharge_overdue:
        apiData.tuition_program_id?.is_surcharge_overdue || false,
      res_partner_bank_id: {
        acc_number:
          apiData.tuition_program_id?.res_partner_bank_id?.acc_number ||
          "UNKNOWN_ACCOUNT",
      },
      tuition_program_parent_id: {
        name:
          apiData.tuition_program_id?.tuition_program_parent_id?.name ||
          "Unknown Program",
      },
    },
    invoices_payment: invoicesPayment,
    amount_total: apiData.amount_total || 0,
    amount_total_words: apiData.amount_total_words || "Zero Vietnamese Dong",
    amount_residual: apiData.amount_residual || 0,
    invoice_date_due: apiData.invoice_date_due || new Date().toISOString(),
    state: apiData.state || "posted",
    payment_state: apiData.invoice_stage || apiData.payment_state || "not_paid",
    is_mix_va: apiData.is_mix_va || false,
    name: apiData.name || "UNKNOWN_INVOICE",
    des_payment: apiData.des_payment || "",
    narration: apiData.narration || "",
    net_total_after_credit: apiData.net_total_after_credit || 0,
    net_total_after_credit_word: apiData.net_total_after_credit_word || "0 VNĐ",
    invoice_payment_total_amount: apiData.invoice_payment_total_amount || 0,
    list_balance_applied: apiData.list_balance_applied || [],
    payment_account_move_transaction_widget:
      apiData.payment_account_move_transaction_widget || [],
    fundUsage: (() => {
      let parsed = [];
      try {
        if (apiData.fund_usage_json) {
          parsed = typeof apiData.fund_usage_json === "string"
            ? JSON.parse(apiData.fund_usage_json)
            : apiData.fund_usage_json;
        }
      } catch (error) {
        console.warn("Error parsing fund_usage_json in convertToOrderData:", error);
      }
      return parsed;
    })(),
  };

  return orderData;
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const addHours = (dateString: any, hours: number): string => {
  if (dateString) {
    const date = new Date(dateString);
    date.setHours(date.getHours() + hours);
    return date.toISOString();
  }
  return "";
};

function compareDates(str1: any, str2: any) {
  return str1 === str2;
}

function isDateBeforeNow(dateTimeStr: string) {
  try {
    const [datePart, timePart] = dateTimeStr.split(" ");
    const [day, month, year] = datePart.split("-").map((num) => parseInt(num));
    const [hours, minutes, seconds] = timePart
      .split(":")
      .map((num) => parseInt(num));

    const inputDate = new Date(year, month - 1, day, hours, minutes, seconds);
    const now = new Date();
    return inputDate.getTime() < now.getTime();
  } catch (error) {
    console.error("Error parsing date:", error);
    return false;
  }
}

const mapData = (data: any[]): DataType[] => {
  return data.map((item, index) => ({
    key: index.toString(),
    order: (index + 1).toString(),
    id: item.id,
    code: item.name,
    narration: item?.narration || "",
    amount: item.amount_total.toString(),
    amount_residual: item.amount_residual,
    status:
      (() => {
        const pState = item.invoice_stage || item.payment_state || "not_paid";
        return pState === "paid" || pState === "reversed"
          ? "paid"
          : pState === "partial"
            ? "partial"
            : pState === "cancelled"
              ? "cancelled"
              : "unpaid";
      })(),
    dateDue: addHours(item?.invoice_date_due || false, 7),
    anumTuition: item?.tuition_program_id?.res_partner_bank_id?.acc_number,
    isOverDue: item?.tuition_program_id?.is_surcharge_overdue,
    semesterName: item?.tuition_program_id?.name,
    paymentUrl: item?.payment_url,
    isSurchargeOverdue: item?.tuition_program_id?.is_surcharge_overdue,
    programDueDate: addHours(item?.tuition_program_id?.due_date || false, 7),
    net_total_after_credit: item?.net_total_after_credit,
    list_balance_applied: item?.list_balance_applied,
  }));
};

const PaymentTable = (props: Props) => {
  const { studentId, partnerId, applicationID } = props;
  const [id, setId] = useState(0);
  const [data, setData] = useState<any>(null);
  console.log("data", data);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isPopupDetailVisible, setIsPopupDetailVisible] = useState(false);
  const [programHover, setProgramHover] = useState<any>();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const [activeTab, setActiveTab] = useState<"receipts" | "refunds">("receipts");
  const [refundData, setRefundData] = useState<any[] | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundPagination, setRefundPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [isRefundPopupDetailVisible, setIsRefundPopupDetailVisible] = useState(false);

  const refundColumns: TableColumnsType<any> = [
    {
      title: "STT",
      dataIndex: "index",
      width: 70,
    },
    {
      title: t("transaction_code") || "Mã giao dịch",
      dataIndex: "name",
    },
    {
      title: t("refund_amount") || "Số tiền hoàn",
      dataIndex: "amount",
      render: (amount) => {
        return (
          <span className="text-[#333] font-medium">
            {Number(amount).toLocaleString("vi-VN")}đ
          </span>
        );
      },
    },
    {
      title: t("time_create") || "Thời gian tạo",
      dataIndex: "create_date",
      render: (create_date) => {
        if (!create_date) return "";
        return formatDateTime(addHours(create_date, 7));
      },
    },

    {
      title: t("refund_method") || "Phương thức hoàn tiền",
      dataIndex: "refund_method",
      render: (method) => {
        if (method === "cash") return t("refund_method_cash") || "Tiền mặt";
        if (method === "transfer") return t("refund_method_transfer") || "Chuyển khoản";
        return method || "";
      },
    },
    {
      title: t("action") || "Hành động",
      fixed: "right",
      render: (_, record: any) => {
        return (
          <div className="flex items-center gap-[16px]">
            <div
              className="w-fit underline text-sm font-medium leading-[16.96px] text-primary cursor-pointer hover:opacity-80 whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                setId(record?.id);
                setIsRefundPopupDetailVisible(true);
              }}
            >
              {t("detail")}
            </div>
          </div>
        );
      },
    },
  ];

  const handleCallButton = async ({ ids }: any) => {
    try {
      // console.log("🔄 Calling API for invoice ID:", ids);

      // Call API với specification đầy đủ
      const response = await ActionService.callButton({
        model: "account.move",
        method: "web_read",
        ids: [ids],
        context: {
          lang: language === "vi" ? "vi_VN" : "en_US",
          tz: "Asia/Saigon",
        },
      });

      // console.log("response", response);

      if (!response) {
        console.error("❌ No response received");
        toast.error("No response from server");
        return;
      }

      let invoiceData = null;

      // Handle response structure based on your API
      if (Array.isArray(response) && response.length > 0) {
        invoiceData = response[0];
        // console.log("✅ SUCCESS: Data from direct response array");
      } else if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        invoiceData = response.data[0];
        // console.log("✅ FALLBACK: Data from response.data array");
      } else if (
        response.data &&
        typeof response.data === "object" &&
        response.data.id
      ) {
        invoiceData = response.data;
        // console.log("✅ FALLBACK: Data from response.data object");
      }

      // console.log("📄 Final extracted invoice data:", invoiceData);

      if (invoiceData && invoiceData.id) {
        try {
          // Convert API data to OrderData format
          const orderData = convertToOrderData(invoiceData);

          // Call printSafely with properly formatted data
          printSafely(orderData);

          // Show success message
          // toast.success("🖨️ Opening print preview...");
        } catch (conversionError) {
          // console.error("💥 Error converting data:", conversionError);
          // toast.error(
          //   "Error processing invoice data. Check console for details."
          // );
        }
      } else {
        // console.log(
        //   "🔍 Response structure:",
        //   JSON.stringify(response, null, 2)
        // );
        // toast.error(
        //   "Invoice data not found. Check console for response structure."
        // );
      }
    } catch (error: any) {
      console.error("💥 Error in handleCallButton:", error);
      if (error?.response) {
        console.error("📄 Error response:", error.response);
      }
      if (error?.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error("An error occurred while fetching invoice data");
      }
    }
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: t("invoice_code"),
      dataIndex: "code",
    },
    {
      title: t("payment_period"),
      dataIndex: "semesterName",
    },
    {
      title: t("amount_payable"),
      dataIndex: "amount_residual",
      sorter: (a: any, b: any) => Number(a.amount_residual) - Number(b.amount_residual),
      render: (amount) => {
        return (
          <span className="text-[#333]">
            {Number(amount).toLocaleString("vi-VN")}đ
          </span>
        );
      },
    },
    {
      title: t("end_time"),
      dataIndex: "dateDue",
      render: (dateDue, record) => {
        const programDueDate = record.programDueDate;
        return compareDates(dateDue, programDueDate) ? (
          <div className="text-left">{formatDateTime(dateDue)}</div>
        ) : (
          <div className="text-left flex items-center gap-[4px]">
            <div
              className="text-[#169C3B] relative"
              onMouseEnter={() => setProgramHover(record.id)}
              onMouseLeave={() => setProgramHover(0)}
            >
              {t("extended_until")}
              {programHover == record.id && (
                <div className="absolute bg-[#5a5959] px-[16px] py-[8px] top-[-35px] left-[50px] rounded-t-[4px] rounded-r-[4px] rounded-b-[4px] rounded-l-none w-[175px] text-center text-[#fff]">
                  {formatDateTime(programDueDate)}
                </div>
              )}
            </div>{" "}
            {formatDateTime(dateDue)}
          </div>
        );
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (status, record) => {
        console.log("record--------------------", record);
        return (
          <div
            className={`flex h-fit w-fit flex-row items-center justify-center gap-2.5 rounded-[48px] px-3 py-1 ${status === "cancelled" || (record?.net_total_after_credit == 0 &&
              (status === "paid" || status === "reversed"))
              ? "bg-[#DEE2E6]"
              : status === "paid" || status === "reversed"
                ? "bg-[#2EB5531A]"
                : isDateBeforeNow(formatDateTime(record.dateDue)) &&
                  status !== "paid"
                  ? "bg-[#ff43191a]"
                  : status === "partial"
                    ? "bg-[#FFE0191A]"
                    : "bg-[rgba(5,152,237,0.12)]"
              }`}
          >
            <div
              className={`h-2 w-2 rounded-full ${status === "cancelled" || (record?.net_total_after_credit == 0 &&
                (status === "paid" || status === "reversed"))
                ? "bg-[#8b8d8e] text-[343A40]"
                : status === "paid" || status === "reversed"
                  ? "bg-[#169C3B] text-[#169C3B]"
                  : isDateBeforeNow(formatDateTime(record.dateDue)) &&
                    status !== "paid"
                    ? "text-[#f5222d] bg-[#f5222d]"
                    : status === "partial"
                      ? "text-[#BAA416] bg-[#BAA416]"
                      : "text-[rgb(5,152,237)] bg-[rgb(5,152,237)]"
                } text-xs font-medium leading-4`}
            ></div>
            <span
              className={`${status === "cancelled" || (record?.net_total_after_credit == 0 &&
                (status === "paid" || status === "reversed"))
                ? "text-[#343A40]"
                : status === "paid" || status === "reversed"
                  ? "text-[#169C3B]"
                  : isDateBeforeNow(formatDateTime(record.dateDue)) &&
                    status !== "paid"
                    ? "text-[#f5222d]"
                    : status === "partial"
                      ? "text-[#BAA416]"
                      : "text-[rgb(5,152,237)]"
                } `}
            >
              {status === "cancelled" || (record?.net_total_after_credit == 0 &&
                (status === "paid" || status === "reversed"))
                ? t("canceled")
                : status === "paid" || status === "reversed"
                  ? t("paid")
                  : isDateBeforeNow(formatDateTime(record.dateDue)) &&
                    status !== "paid"
                    ? t("overdue")
                    : status === "partial"
                      ? t("partial_payment")
                      : t("not_paid")}
            </span>
          </div>
        );
      },
    },
    {
      title: t("action"),
      dataIndex: "qr_string",
      fixed: "right",
      render: (_, record: any) => {
        return (
          <div className="flex items-center gap-[16px]">
            <div
              className="flex items-center justify-start"
              onClick={(e) => e.stopPropagation()}
            >
              {(record?.status !== "paid" &&
                !record?.isOverDue &&
                isDateBeforeNow(formatDateTime(record.dateDue))) ||
                record?.status === "paid" ? (
                <div className="flex items-center gap-[16px]">
                  {/* <p
                    className="w-fix underline text-sm font-medium leading-[16.96px] text-primary cursor-pointer"
                    onClick={() => {
                      setId(record?.id);
                      handleDetailClick();
                    }}
                  >
                    {t("detail")}
                  </p> */}
                  <button
                    onClick={() => {
                      // console.log("🖨️ Print button clicked for record:", record);
                      const ids = record?.id;
                      handleCallButton({ ids });
                    }}
                    style={{
                      color: "#696969",
                      fontSize: "14px",
                      padding: "5px 12px",
                      border: "1px solid #696969",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      cursor: "pointer",
                    }}
                    className="font-medium disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-50 w-[108px]"
                  >
                    {t("tuition_management_print_invoice")}
                  </button>
                </div>
              ) : (
                <button
                  className="h-8 w-[108px] rounded-lg bg-[#20B73B] px-4 text-sm font-medium leading-[16.96px] text-white hover:bg-[#318b23] disabled:bg-[#DEE2E6] disabled:text-[#8b8d8e] disabled:cursor-not-allowed"
                  onClick={() => handlePayClick(record?.paymentUrl)}
                  disabled={record?.status === "cancelled"}
                >
                  {t("payment1")}
                </button>
              )}
            </div>
            <div
              className="w-fit underline text-sm font-medium leading-[16.96px] text-primary cursor-pointer hover:opacity-80 whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                setId(record?.id);
                handleDetailClick();
              }}
            >
              {t("detail")}
            </div>
          </div>
        );
      },
    },
  ];
  const { env }: any = useConfigEnv();

  const fetchRefundData = useCallback(
    async (current: number, pageSize: number) => {
      setRefundLoading(true);
      try {
        const response = await fetch(`${env?.apiCall}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "refund.payment",
            method: "web_search_read",
            ids: [],
            kwargs: {
              specification: {
                id: {},
                name: {},
                partner_id: {
                  fields: {
                    display_name: {},
                  },
                },
                amount: {},
                currency_id: {
                  fields: {
                    display_name: {},
                  },
                },
                refund_date: {},
                refund_source_summary: {},
                refund_method: {},
                create_date: {},
                create_uid: {
                  fields: {
                    display_name: {},
                  },
                },
              },
              offset: (current - 1) * pageSize,
              order: "create_date DESC",
              limit: pageSize,
              count_limit: 10001,
              domain: [
                ...(studentId !== "false"
                  ? [["partner_id.stud_id", "=", studentId]]
                  : applicationID !== "false"
                    ? [["partner_id.tmp_stud_id", "=", applicationID]]
                    : [["partner_id", "=", partnerId]]),
              ],
            },
          }),
        });

        if (response.status === 200) {
          const res = await response.json();
          const mapped = (res?.records || []).map((item: any, index: number) => ({
            key: item.id?.toString() || index.toString(),
            id: item.id,
            index: ((current - 1) * pageSize + index + 1).toString(),
            name: item.name,
            amount: item.amount,
            refund_method: item.refund_method,
            create_date: item.create_date,
            create_uid: item.create_uid?.display_name || "",
          }));
          setRefundData(mapped);
          setRefundPagination((prev) => ({
            ...prev,
            total: res.length || 0,
            current,
            pageSize,
          }));
        } else {
          setRefundData([]);
        }
      } catch (error) {
        console.error("Error fetching refund data:", error);
        setRefundData([]);
      } finally {
        setRefundLoading(false);
      }
    },
    [studentId, partnerId, applicationID, env?.apiCall]
  );

  const fetchData = useCallback(
    async (current: any, pageSize: any) => {
      setLoading(true);
      try {
        const response = await fetch(`${env?.apiCall}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "account.move",
            method: "web_search_read",
            ids: [],
            kwargs: {
              specification: {
                id: {},
                name: {},
                narration: {},
                amount_residual: {},
                amount_total: {},
                state: {},
                payment_state: {},
                invoice_stage: {},
                payment_url: {},
                net_total_after_credit: {},
                list_balance_applied: {},
                invoice_date_due: {},
                payment_account_move_transaction_widget: {},
                tuition_program_id: {
                  fields: {
                    name: {},
                    is_surcharge_overdue: {},
                    tuition_program_parent_id: {
                      fields: {
                        name: {},
                      },
                    },
                    res_partner_bank_id: {
                      fields: {
                        acc_number: {},
                      },
                    },
                    due_date: {},
                  },
                },
                partner_id: {
                  fields: {
                    display_name: {},
                    image_1920: {},
                    stud_id: {},
                    total_amount_outstanding: {},
                    standard: {
                      fields: {
                        display_name: {},
                      },
                    },
                    div: {
                      fields: {
                        display_name: {},
                      },
                    },
                    birthdate: {},
                  },
                },
                write_date: {},
              },
              offset: (current - 1) * pageSize,
              order: "",
              limit: pageSize,
              count_limit: 10001,
              domain: [
                ...(studentId !== "false"
                  ? [["partner_id.stud_id", "=", studentId]]
                  : applicationID !== "false"
                    ? [["partner_id.tmp_stud_id", "=", applicationID]]
                    : [["partner_id", "=", partnerId]]),
                ["move_type", "=", "out_invoice"],
                ["state", "=", "posted"],
                ["tuition_program_id.is_show_student", "=", true],
              ],
            },
          }),
        });

        if (response.status === 200) {
          const res = await response.json();
          const mappedData = mapData(res?.records || []);
          setData(mappedData);
          setPagination((prev) => ({
            ...prev,
            total: res.length || 0,
            current,
            pageSize,
          }));
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [studentId, partnerId, applicationID, env?.apiCall]
  );

  useEffect(() => {
    if (activeTab === "receipts") {
      fetchData(pagination.current, pagination.pageSize);
    } else {
      fetchRefundData(refundPagination.current, refundPagination.pageSize);
    }
  }, [fetchData, fetchRefundData, pagination.current, pagination.pageSize, refundPagination.current, refundPagination.pageSize, studentId, activeTab]);

  const handleTableChange = (newPagination: any, filters: any, sorter: any) => {
    fetchData(newPagination.current, newPagination.pageSize);
  };

  const handlePayClick = (paymentUrl: string) => {
    window.location.href = `${paymentUrl}&lang=${language == "vi" ? "vi" : "en"
      }`;
  };

  const handleDetailClick = () => {
    setIsPopupDetailVisible(true);
  };

  const handleCloseDetailPopup = () => {
    setIsPopupDetailVisible(false);
  };

  const handleOutdateClick = () => {
    setIsPopupVisible(true);
  };

  const handleCloseOutdatePopup = () => {
    setIsPopupVisible(false);
  };

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseOutdatePopup();
      handleCloseDetailPopup();
    }
  };

  const handleContact = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const handleOutsideRefundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setIsRefundPopupDetailVisible(false);
    }
  };

  return (
    <>
      <div className="flex gap-4 p-4 border-b border-[#EBEBEB] bg-[#FCFDFE] rounded-t-xl flex-wrap">
        <button
          onClick={() => setActiveTab("receipts")}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === "receipts"
            ? "bg-[#F5F7FA] text-[#2D2D2D] border border-[#E2E4E9] shadow-sm font-bold"
            : "text-[#666666] hover:bg-gray-50 border border-transparent"
            }`}
        >
          {t("payment_receipt_list")}
        </button>
        <button
          onClick={() => setActiveTab("refunds")}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === "refunds"
            ? "bg-[#F5F7FA] text-[#2D2D2D] border border-[#E2E4E9] shadow-sm font-bold"
            : "text-[#666666] hover:bg-gray-50 border border-transparent"
            }`}
        >
          {t("refund_transaction_list")}
        </button>
      </div>

      {activeTab === "receipts" ? (
        <Table
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          loading={loading}
          locale={{ emptyText: t("no_data") }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: (e) => {
              const selection = window.getSelection();
              if (selection && selection.toString()) {
                return;
              }
              setId(record?.id);
              handleDetailClick();
            },
          })}
        />
      ) : (
        <Table
          columns={refundColumns}
          dataSource={refundData || []}
          pagination={refundPagination}
          onChange={(newPag: any) => {
            fetchRefundData(newPag.current, newPag.pageSize);
          }}
          scroll={{ x: "max-content" }}
          loading={refundLoading}
          locale={{ emptyText: t("no_data") }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: (e) => {
              const selection = window.getSelection();
              if (selection && selection.toString()) {
                return;
              }
              setId(record?.id);
              setIsRefundPopupDetailVisible(true);
            },
          })}
        />
      )}

      {isPopupVisible && (
        <div
          className="fixed z-[999999999] inset-0 flex items-center justify-center bg-black bg-opacity-50 px-2"
          onClick={handleOutsideClick}
        >
          <div className="relative flex flex-col items-center gap-6 rounded-2xl bg-white p-8">
            <img
              src="/images/payment/close-fill.svg"
              alt=""
              className="absolute right-4 top-4 cursor-pointer"
              onClick={handleCloseOutdatePopup}
            />
            <img src="/images/payment/oudate-img.svg" alt="" />
            <div className="mx-2 flex max-w-[310px] flex-col gap-2 sm:mx-14 md:mx-14 lg:mx-14">
              <h1 className="text-center text-xl font-semibold leading-[18px]">
                {t("tuition_fee_has_expired")}
              </h1>
              <p className="text-center text-sm font-normal">
                {t("contact_your_school")}
              </p>
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                className="rounded-lg border-[1px] border-[#E2E4E9] bg-transparent px-6 py-[12.5px] text-base text-[#2D2D2D] hover:bg-[#E2E4E9]"
                onClick={() => handleContact("email")}
              >
                {t("contact")}
              </button>
              <button
                className="rounded-lg bg-primary px-6 py-[12.5px] text-base text-white hover:bg-[#318b23]"
                onClick={handleCloseOutdatePopup}
              >
                {t("got_it")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isPopupDetailVisible && (
        <PaymentDetail
          id={id}
          handleCloseDetailPopup={handleCloseDetailPopup}
          handleOutsideClick={handleOutsideClick}
        />
      )}

      {isRefundPopupDetailVisible && (
        <RefundDetail
          id={id}
          handleCloseDetailPopup={() => setIsRefundPopupDetailVisible(false)}
          handleOutsideClick={handleOutsideRefundClick}
        />
      )}
    </>
  );
};

export default PaymentTable;
